import * as React from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../colors/color';
import { getIconSvg, iconName } from '../icons/Icons';
import { HeaderProps, h1Styling, h2Styling, h3Styling, h4Styling } from '../typography/Heading';
import { defaultInputWrapperCss, disabledInputWrapperCss, errorStateInputWrapperCss, inputBorderRadius } from './sharedContainerStyles';


const inputStyleModes = ['default', 'H1', 'H2', 'H3', 'H4'] as const;
export type InputStyleMode = typeof inputStyleModes[number];



const inputSizes = ['32px', '42px', '52px', 'auto'] as const;
export type InputSize = typeof inputSizes[number];


export function textInputPadding(fontSize?:14 | 16): {paddingVertical:number, paddingHorizontal:number} 
{
    return {
        paddingVertical: fontSize === 14 ? 4 : 10,
        paddingHorizontal: fontSize === 14 ? 10 : 15
    }
}

// TODO
// Replace inErrorState with displayState:'default'|'error'
export type TextInputProps = {
    id?: string,
    value: string,
    placeholder?:string,
    autoComplete?: string,
    type?: 'text' | 'password' | 'email',
    disabled?: boolean,
    inErrorState?:boolean,
    tabIndex?: number,
    leadingIcon?:iconName,
    trailingIcon?:iconName,
    trailingIconButton?:iconName,
    dataCy?:string,
    onChange: (value: string) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void,
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    innerRef?:React.RefObject<HTMLInputElement>,
    styleMode?:InputStyleMode,
    ariaLabel?:string,
    fontSize?: 14 | 16,
    size?: InputSize,
    verticalPaddingOverride?: number,
    horizontalPaddingOverride?: number,
}

export function TextInput(props: TextInputProps): JSX.Element 
{
    //const [focus, setFocus] = React.useState(false);

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        props.onChange(event.target.value);
    }

    function handleFocus(e:React.FocusEvent<HTMLInputElement>)
    {
        //setFocus(true);
        if(props.onFocus)
            props.onFocus(e);
    }

    function handleBlur(e:React.FocusEvent<HTMLInputElement>)
    {
        //setFocus(false);
        if(props.onBlur)
            props.onBlur(e);
    }

    const disabled = props.disabled === undefined ? false : props.disabled;
    const inErrorState = props.inErrorState === undefined ? false : props.inErrorState;

    const activeStyleMode = props.styleMode || 'default';

    let { paddingVertical, paddingHorizontal } = textInputPadding(props.fontSize);
    if(props.verticalPaddingOverride !== undefined) {
        paddingVertical = props.verticalPaddingOverride;
    }
    if(props.horizontalPaddingOverride !== undefined) {
        paddingHorizontal = props.horizontalPaddingOverride;
    }

    return (
        <InputWrapper 
            className="textInputAreaWrapper"
            disabled={disabled} 
            inErrorState={inErrorState} 
            fontSize={props.fontSize || 16} 
            size={props.size || "42px"}
            paddingVertical={paddingVertical} 
            paddingHorizontal={paddingHorizontal} 
            styleMode={activeStyleMode}>
            
            {props.leadingIcon && 
                <TextInputAreaIconContainer disabled={disabled} styleMode={activeStyleMode}>
                    {getIconSvg(props.leadingIcon)}
                </TextInputAreaIconContainer>
            }

            <input
                aria-label={props.ariaLabel}
                autoComplete={props.autoComplete}
                data-cy={props.dataCy}
                disabled={props.disabled}
                className="textInput"
                id={props.id}
                name="text-input"
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={props.onKeyDown}
                placeholder={props.placeholder}
                ref={props.innerRef}
                tabIndex={props.tabIndex}
                type={props.type}
                value={props.value} />
                
            {props.trailingIcon &&
                <TextInputAreaIconContainer disabled={disabled} styleMode={activeStyleMode}>
                    {getIconSvg(props.trailingIcon)}
                </TextInputAreaIconContainer>
            }
        </InputWrapper>
    );
}

const commonInputWrapper = css<{disabled:boolean, inErrorState:boolean, size:InputSize}>`
    background: white;
    display: flex;
    flex-direction: row;
    gap:0;
    border-radius: ${inputBorderRadius};
    height: ${props => props.size};
    

    & > input, textarea 
    {
        font-family: "Noto Sans", sans-serif;
        width:100%;
        line-height: 1.4;
        background: transparent;
        outline: none;
        border:none;
        color:${props => props.disabled
            ? colors.inputState.disabled.text
            : (props.inErrorState 
                ? colors.inputState.error.text 
                : colors.text.normal
            )
        };
    }

    & > textarea {
        resize: none;
        word-wrap:break-word;
    }

    & > input::placeholder{
        color:${colors.text.inactive};
    }

    ${props => props.disabled
        ? disabledInputWrapperCss
        : (props.inErrorState ? errorStateInputWrapperCss : defaultInputWrapperCss)
    }
`;

const defaultInputWrapper = css<{disabled:boolean, inErrorState:boolean, fontSize:number, paddingVertical:number, paddingHorizontal:number, size:InputSize}>`
    ${commonInputWrapper};

    
    & > input, textarea {
        font-size: ${props => props.fontSize}px;      
        padding: ${props => props.paddingVertical}px ${props => props.paddingHorizontal}px;
    }
`;

const headerStyleInputWrapper = css<HeaderProps & {disabled:boolean, inErrorState:boolean, fontSize:number, paddingVertical:number, paddingHorizontal:number, styleMode:InputStyleMode, size:InputSize}>`
    
    & > input, textarea {
        font-size: 16px;
        padding: 10px 5px;
        
        &:first-child{
            padding: 10px 15px;
        }
        ${props => props.styleMode === 'H1' && h1Styling};
        ${props => props.styleMode === 'H2' && h2Styling};
        ${props => props.styleMode === 'H3' && h3Styling};
        ${props => props.styleMode === 'H4' && h4Styling};
    }

    // Explicitly called after to override colors from heading styles
    ${commonInputWrapper}
`;

export const InputWrapper = styled.div<{disabled:boolean, inErrorState:boolean, fontSize:number, paddingVertical:number, paddingHorizontal:number, styleMode:InputStyleMode, size:InputSize}>`
    ${props => props.styleMode === 'default' ? defaultInputWrapper : headerStyleInputWrapper}
`;

const defaultIconContainer = css<{disabled:boolean}>`
      
    flex: 0 0 22px;
    height:22px;
    align-self: center;
    justify-self: center;
    &:first-child {
        margin-left:5px;
    }
    &:last-child {
        margin-left:5px;
        margin-right:5px;
    }
    & > svg {
        padding:0px;
        display: block;
        width:22px;
        height:22px;
        fill: ${props => props.disabled ? colors.icon.passive : colors.icon.default};
    }
`;

const headerStyleIconContainer = css<{disabled:boolean}>`
flex: 0 0 22px;
    height:22px;
    align-self: center;
    justify-self: center;
    &:first-child {
        margin-left:5px;
    }
    &:last-child {
        margin-left:5px;
        margin-right:5px;
    }
    & > svg {
        padding:0px;
        display: block;
        width:22px;
        height:22px;
        fill: ${props => props.disabled ? colors.text.light : colors.text.normal};
    }
`;

export const TextInputAreaIconContainer = styled.div<{disabled:boolean, styleMode:InputStyleMode}>`
    ${props => props.styleMode === 'default' ? defaultIconContainer : headerStyleIconContainer}
`;